import axios from 'axios';
import type {
  LiensListResponse,
  LienData,
  CreateLienRequest,
  UpdateLienRequest,
  VendorData,
  VendorListResponse,
  CreateVendorRequest,
  LienPagination,
  LienStatusUpdateResponse,
  LienBulkResponse,
  RawLienBulkResponse,
} from '../../models/lienTypes';

/** Normalize a raw bulk response — push sends `pushed`, undo sends `undone`. */
const normalizeBulk = (data: RawLienBulkResponse): LienBulkResponse => ({
  succeeded: data.pushed ?? data.undone ?? 0,
  failed: data.failed ?? 0,
  skipped: data.skipped ?? 0,
  total: data.total ?? 0,
  results: data.results ?? [],
});

const buildUrl = (matterId: string): string => `/matters/${matterId}/liens`;

export const getLiens = async (
  matterId: string,
  pagination: LienPagination,
): Promise<LiensListResponse> => {
  const { data } = await axios.get<LiensListResponse>(buildUrl(matterId), {
    params: {
      offset: pagination.offset,
      limit: pagination.limit,
      orderBy: pagination.orderBy,
      sortAscending: pagination.sortAscending,
    },
  });
  return data;
};

export const getLien = async (
  lienId: string,
): Promise<LienData> => {
  const { data } = await axios.get<LienData>(`/liens/${lienId}`);
  return data;
};

export const createLien = async (
  matterId: string,
  payload: CreateLienRequest,
): Promise<LienData> => {
  const { data } = await axios.post<LienData>(buildUrl(matterId), payload);
  return data;
};

export const updateLien = async (
  lienId: string,
  payload: UpdateLienRequest,
): Promise<LienData> => {
  const { data } = await axios.put<LienData>(`/liens/${lienId}`, payload);
  return data;
};

export const getVendors = async (
  search?: string,
): Promise<VendorData[]> => {
  const { data } = await axios.get<VendorListResponse>('/vendors', {
    params: { search: search || undefined },
  });
  return data.vendors ?? [];
};

export const createVendor = async (
  payload: CreateVendorRequest,
): Promise<VendorData> => {
  const { data } = await axios.post<VendorData>('/vendors', payload);
  return data;
};

export const deleteLien = async (
  lienId: string,
): Promise<void> => {
  await axios.delete(`/liens/${lienId}`);
};

export const updateLienStatus = async (
  lienId: string,
  status: string,
): Promise<LienStatusUpdateResponse> => {
  const { data } = await axios.patch<LienStatusUpdateResponse>(
    `/liens/${lienId}/status`,
    { status },
  );
  return data;
};

// ── QBO Push / Undo ────────────────────────────────────────────────────────
//
// Bulk operations call a SINGLE backend endpoint with all selected ids — the
// backend owns partial-success semantics and status transitions. The frontend
// must NEVER loop individual push/undo requests (see process.service.js for the
// legacy anti-pattern this deliberately avoids).

/** Push a single finalized lien to QBO, creating a trust check. */
export const pushLien = async (
  matterId: string,
  lienId: string,
): Promise<LienData> => {
  const { data } = await axios.post<LienData>(
    `${buildUrl(matterId)}/${lienId}/push`,
  );
  return data;
};

/** Push many selected liens to QBO in one backend call. */
export const bulkPushLiens = async (
  matterId: string,
  lienIds: string[],
): Promise<LienBulkResponse> => {
  const { data } = await axios.post<RawLienBulkResponse>(
    `${buildUrl(matterId)}/bulk-push`,
    { lienIds },
  );
  return normalizeBulk(data);
};

/** Undo a single push, deleting the QBO trust check. */
export const undoPush = async (
  matterId: string,
  lienId: string,
): Promise<LienData> => {
  const { data } = await axios.post<LienData>(
    `${buildUrl(matterId)}/${lienId}/push/undo`,
  );
  return data;
};

/** Undo many pushed liens in one backend call. */
export const bulkUndoPush = async (
  matterId: string,
  lienIds: string[],
): Promise<LienBulkResponse> => {
  const { data } = await axios.post<RawLienBulkResponse>(
    `${buildUrl(matterId)}/bulk-undo`,
    { lienIds },
  );
  return normalizeBulk(data);
};
